import rawFeatureConfig from './features.json'

declare global {
  interface Window {
    __cyFeatureFlags?: string[]
  }
}

type RawFeatureEntry =
  | boolean
  | {
      enabled: boolean
      permissionPrefixes?: string[]
      permissionNames?: string[]
    }

type NormalizedFeatureEntry = {
  enabled: boolean
  permissionPrefixes: string[]
  permissionNames: string[]
}

type FeatureConfig = {
  [K in keyof typeof rawFeatureConfig]: NormalizedFeatureEntry
}

const normalizeFeatureEntry = (value: RawFeatureEntry): NormalizedFeatureEntry => {
  if (typeof value === 'boolean') {
    return {
      enabled: value,
      permissionPrefixes: [],
      permissionNames: [],
    }
  }

  return {
    enabled: Boolean(value.enabled),
    permissionPrefixes: Array.isArray(value.permissionPrefixes) ? value.permissionPrefixes : [],
    permissionNames: Array.isArray(value.permissionNames) ? value.permissionNames : [],
  }
}

const featureConfigBase = Object.entries(rawFeatureConfig).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: normalizeFeatureEntry(value as RawFeatureEntry),
  }),
  {} as FeatureConfig
)

export type FeatureKey = keyof FeatureConfig

const getWindowFeatureOverrides = (): FeatureKey[] | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const overrides = window.__cyFeatureFlags
  if (!Array.isArray(overrides) || overrides.length === 0) {
    return null
  }

  const validOverrides = overrides.filter(
    (feature): feature is FeatureKey => feature in featureConfigBase
  )

  if (validOverrides.length === 0) {
    return null
  }

  return Array.from(new Set(validOverrides))
}

let cachedFeatureConfig: FeatureConfig | null = null
let cachedOverrideSignature: string | null = null

const computeOverrideSignature = (overrides: FeatureKey[]): string =>
  overrides.slice().sort().join('|')

const applyOverrides = (overrides: FeatureKey[]): FeatureConfig => {
  const overrideSet = new Set(overrides)
  return Object.entries(featureConfigBase).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: {
        ...value,
        enabled: overrideSet.has(key as FeatureKey),
      },
    }),
    {} as FeatureConfig
  )
}

const getActiveFeatureConfig = (): FeatureConfig => {
  const overrides = getWindowFeatureOverrides()
  if (!overrides) {
    cachedFeatureConfig = null
    cachedOverrideSignature = null
    return featureConfigBase
  }

  const signature = computeOverrideSignature(overrides)
  if (cachedFeatureConfig && signature === cachedOverrideSignature) {
    return cachedFeatureConfig
  }

  cachedFeatureConfig = applyOverrides(overrides)
  cachedOverrideSignature = signature
  return cachedFeatureConfig
}

export const getFeatureConfig = (): FeatureConfig => getActiveFeatureConfig()

export const isFeatureEnabled = (feature: FeatureKey): boolean =>
  getActiveFeatureConfig()[feature]?.enabled ?? false

const buildFeatureConfigSignature = (config: FeatureConfig): string =>
  (Object.keys(config) as FeatureKey[])
    .sort()
    .map(featureKey => `${featureKey}:${config[featureKey].enabled ? 1 : 0}`)
    .join('|')

export const getFeatureConfigSignature = (): string =>
  buildFeatureConfigSignature(getFeatureConfig())

export const createPermissionMatcher = (): ((permissionName: string) => boolean) => {
  const activeConfig = getFeatureConfig()
  const enabledFeatures = new Set<FeatureKey>(
    Object.entries(activeConfig)
      .filter(([, value]) => value.enabled)
      .map(([featureKey]) => featureKey as FeatureKey)
  )

  return (permissionName: string): boolean => {
    const directMatchFeatures = permissionNameToFeatures[permissionName]
    if (directMatchFeatures && directMatchFeatures.length > 0) {
      return directMatchFeatures.some(feature => enabledFeatures.has(feature))
    }

    const prefix = permissionName.split(':')[0]
    if (!prefix) {
      return true
    }

    const prefixMatchFeatures = permissionPrefixToFeatures[prefix]
    if (prefixMatchFeatures && prefixMatchFeatures.length > 0) {
      return prefixMatchFeatures.some(feature => enabledFeatures.has(feature))
    }

    return true
  }
}

const permissionNameToFeatures = Object.entries(featureConfigBase).reduce<Record<string, FeatureKey[]>>(
  (acc, [featureKey, config]) => {
    config.permissionNames.forEach(permissionName => {
      const existing = acc[permissionName] ?? []
      acc[permissionName] = [...existing, featureKey as FeatureKey]
    })
    return acc
  },
  {}
)

const permissionPrefixToFeatures = Object.entries(featureConfigBase).reduce<Record<string, FeatureKey[]>>(
  (acc, [featureKey, config]) => {
    config.permissionPrefixes.forEach(prefix => {
      const existing = acc[prefix] ?? []
      acc[prefix] = [...existing, featureKey as FeatureKey]
    })
    return acc
  },
  {}
)

const toFeatureArray = (feature: FeatureKey | FeatureKey[] | undefined): FeatureKey[] => {
  if (!feature) return []
  return Array.isArray(feature) ? feature : [feature]
}

const hasEnabledFeature = (features: FeatureKey[]): boolean =>
  features.some(feature => isFeatureEnabled(feature))

export const isPermissionEnabled = (permissionName: string): boolean => {
  const directMatchFeatures = permissionNameToFeatures[permissionName]
  if (directMatchFeatures && directMatchFeatures.length > 0) {
    return hasEnabledFeature(directMatchFeatures)
  }

  const prefix = permissionName.split(':')[0]
  if (!prefix) {
    return true
  }

  const prefixMatchFeatures = permissionPrefixToFeatures[prefix]
  if (prefixMatchFeatures && prefixMatchFeatures.length > 0) {
    return hasEnabledFeature(prefixMatchFeatures)
  }

  return true
}

export const getDisabledPermissionNames = (permissionNames: string[]): string[] =>
  permissionNames.filter(permissionName => !isPermissionEnabled(permissionName))
